import React, { useEffect, useState } from 'react'
import { Image, ImageStyle, TouchableOpacity, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@src/components/base'
import { thresholdsSettingsSelector, unitSystemStoreStateSelector } from '@src/selectors/settings'
import { Analytics, CustomEventTypes } from '@src/config'
import { MobileAppFeature } from '@src/types'
import { User } from '@src/utils'
import { UpsellCGMBanner } from '@src/screens/Home/components/Banners/UpsellCGMBanner'
import { blurredDialChartImage, poweredBySteloLogoImage } from '@src/assets/images'
import { useIsDarkMode } from '@src/config/theme'
import { useShowByosIntegrationWarning } from '@src/screens/OwnSensorOnboarding/hooks/useShowByosIntegrationWarning'
import { ByosIntegrationWarning } from '@src/components/Timeline/ByosIntegrationWarning'
import { UnitSystem } from '@src/screens/Settings'
import { usePrimaryStatsData } from '@src/screens/Home/hooks/usePrimaryStatsData'
import { useShowDexcomSteloLogo } from '@src/utils/hooks'
import { GlucoseArcChart } from './GlucoseArcChart'

const GRAPH_LIMITS_PADDING = 50
const TIMESTAMP_REFRESH_INTERVAL = 5000
const HOME_SCREEN_CHART_CARD_HEIGHT = 232

export const DialChart = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation()
  const [_intervalId, setIntervalId] = useState<NodeJS.Timeout>()
  const { glucoseLowThreshold, glucoseHighThreshold } = useSelector(thresholdsSettingsSelector)
  const unitSystem = useSelector(unitSystemStoreStateSelector)
  const { primaryStatDisplayValue, primaryStatValue, lastReadingTime } = usePrimaryStatsData({
    displayLastReading: true,
  })
  const canViewGlucoseCharts = User.hasFeature(MobileAppFeature.ViewGlucoseCharts)
  const isDarkMode = useIsDarkMode()
  const showLogo = useShowDexcomSteloLogo()

  const { thirdPartyWarning } = useShowByosIntegrationWarning()
  const showByosIntegrationWarning = thirdPartyWarning
  const glucoseUnits = unitSystem === UnitSystem.Metric ? 'mmol/L' : 'mg/dL'

  // rerender component every 5 seconds so that the timestamp is updated
  useEffect(() => {
    if (!canViewGlucoseCharts || showByosIntegrationWarning) {
      return
    }
    const interval = setInterval(() => {
      setIntervalId(interval)
    }, TIMESTAMP_REFRESH_INTERVAL)

    return () => clearInterval(interval)
  }, [canViewGlucoseCharts, showByosIntegrationWarning])

  const handleDetailsButtonPress = () => {
    Analytics.track(CustomEventTypes.HomeScreenDialChartSeeDetailsTapped)
    navigation.navigate('EventsListScreen', { resetFilters: true })
  }

  if (!canViewGlucoseCharts) {
    return (
      <UpsellCGMBanner
        image={
          <Image
            style={styles.blurredChartImage as ImageStyle}
            source={blurredDialChartImage(isDarkMode).imageSource}
          />
        }
      />
    )
  }

  if (showByosIntegrationWarning) {
    return (
      <View style={styles.container}>
        <Image
          style={styles.blurredChartImage as ImageStyle}
          source={blurredDialChartImage(isDarkMode).imageSource}
        />
        <ByosIntegrationWarning thirdPartyWarning={thirdPartyWarning} />
      </View>
    )
  }

  const validGlucoseValue = !isNaN(Number(primaryStatValue))

  return (
    <View style={styles.container}>
      {showLogo && (
        <View style={[styles.logoContainer]}>
          <Image
            source={poweredBySteloLogoImage(isDarkMode).imageSource}
            style={styles.logoStyle as ImageStyle}
          />
        </View>
      )}
      <TouchableOpacity
        accessibilityLabel="Glucose Response card"
        onPress={handleDetailsButtonPress}
      >
        <GlucoseArcChart
          title={validGlucoseValue ? glucoseUnits : 'No Data'}
          timestamp={lastReadingTime ? lastReadingTime.fromNow() : ''}
          value={primaryStatValue || undefined}
          displayValue={primaryStatDisplayValue || undefined}
          minValue={glucoseLowThreshold - GRAPH_LIMITS_PADDING}
          maxValue={glucoseHighThreshold + GRAPH_LIMITS_PADDING}
        />
        <TouchableOpacity
          accessibilityLabel="see daily details"
          onPress={handleDetailsButtonPress}
          style={styles.detailsButton}
        >
          <Text type="regular" bold>
            See Details
          </Text>
        </TouchableOpacity>
      </TouchableOpacity>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    height: HOME_SCREEN_CHART_CARD_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 16,
  },
  logoContainer: {
    position: 'absolute',
    right: -8,
    top: 8,
  },
  logoStyle: {
    width: 72,
    height: 18,
    resizeMode: 'contain',
  },
  blurredChartImage: {
    height: HOME_SCREEN_CHART_CARD_HEIGHT,
    width: '100%',
  },
  detailsButton: {
    backgroundColor: 'theme.secondary.base',
    alignSelf: 'center',
    justifyContent: 'center',
    height: 32,
    width: 'auto',
    borderRadius: 24,
    paddingHorizontal: 16,
    marginTop: 8,
    marginBottom: 16,
    alignItems: 'center',
  },
})
